import type {
  Kmat,
  LinearConfiguration,
  LinearStep,
  LinearSubstep,
  SubstepCstics,
  SubstepTile,
} from "~/features/configurator/Linear/models";
import type {
  ConfiguratorDefinition,
  CsticDefaultValues,
  CsticDefinition,
  CsticToSet,
  OptionDefinition,
  ResolvedCsticDefinition,
  StepDefinition,
  SubstepDefinition,
} from "~/features/configurator/common/v2/StepDefinition";
import { RailsDefinition } from "~/features/configurator/Rail/v2/configuration/classic/RailConfiguratorDefinition";
import { CarriagesDefinition } from "~/features/configurator/Carriage/v2/configuration/CarriageConfiguratorDefinition";
import { KWLTCarriagesDefinition } from "~/features/configurator/Carriage/v2/configuration/kwlt/KWLTCarriageConfiguratorDefinition";
import { LCValueResolver } from "~/features/configurator/common/v2/types";
import { isNonNull, toMapFn } from "~/common/utils/reducers";
import { ImperialShaftConfiguratorDefinition } from "~/features/configurator/Shaft/v2/configuration/ImperialShaftConfiguratorDefinition";
import { MetricShaftConfiguratorDefinition } from "~/features/configurator/Shaft/v2/configuration/MetricShaftConfiguratorDefinition";
import {
  CARRIAGE_CONFIGURATOR_PRODUCT_CODE,
  INCH_SHAFT_CONFIGURATOR_PRODUCT_CODE,
  KLLT_UNIT_CONFIGURATOR_PRODUCT_CODE,
  KWLT_CARRIAGE_CONFIGURATOR_PRODUCT_CODE,
  METRIC_SHAFT_CONFIGURATOR_PRODUCT_CODE,
  RAIL_CONFIGURATOR_PRODUCT_CODE,
  TKDM_RAIL_CONFIGURATOR_PRODUCT_CODE,
  TKLT_RAIL_CONFIGURATOR_PRODUCT_CODE,
  UNIT_CONFIGURATOR_PRODUCT_CODE,
} from "~/domains/configurator/constants";

import { TKDMRailsDefinition } from "~/features/configurator/Rail/v2/configuration/tkdm/TKDMRailConfiguratorDefinition";

import { TKLTRailsDefinition } from "~/features/configurator/Rail/v2/configuration/tklt/TKLTRailConfiguratorDefinition";
import { UnitConfiguratorDefinition } from "~/features/configurator/Linear/v2/configuration/classic/UnitConfiguratorDefinition";
import { KLLTUnitConfiguratorDefinition } from "~/features/configurator/Linear/v2/configuration/kllt/KLLTUnitConfiguratorDefinition";
import { KmatType } from "~/features/configurator/Linear/kmatType";
import { getMainKmat } from "~/features/configurator/Shaft/common/commonShaftHelpers";

const DEFINITION_MAP: Record<string, ConfiguratorDefinition> = {
  [RAIL_CONFIGURATOR_PRODUCT_CODE]: RailsDefinition,
  [TKDM_RAIL_CONFIGURATOR_PRODUCT_CODE]: TKDMRailsDefinition,
  [TKLT_RAIL_CONFIGURATOR_PRODUCT_CODE]: TKLTRailsDefinition,
  [CARRIAGE_CONFIGURATOR_PRODUCT_CODE]: CarriagesDefinition,
  [KWLT_CARRIAGE_CONFIGURATOR_PRODUCT_CODE]: KWLTCarriagesDefinition,
  [UNIT_CONFIGURATOR_PRODUCT_CODE]: UnitConfiguratorDefinition,
  [KLLT_UNIT_CONFIGURATOR_PRODUCT_CODE]: KLLTUnitConfiguratorDefinition,
  [METRIC_SHAFT_CONFIGURATOR_PRODUCT_CODE]: MetricShaftConfiguratorDefinition,
  [INCH_SHAFT_CONFIGURATOR_PRODUCT_CODE]: ImperialShaftConfiguratorDefinition,
};

export function applyConfigurationFromDefinition(
  configuration: LinearConfiguration,
  kmats: Record<KmatType, Kmat[]>,
  externalDefaultValues?: CsticDefaultValues,
) {
  const productCode = configuration.productCode;
  // WIP on replacing BE configuration with ConfiguratorDefinition
  const definition: ConfiguratorDefinition | null = DEFINITION_MAP[productCode] ?? null;

  const mainKmat = getMainKmat({ kmats });
  const subKmats = [...(kmats[KmatType.SubRail] ?? []), ...(kmats[KmatType.SubCarriage] ?? [])];

  const mainResolver = new LCValueResolver({
    mainKmat,
    currentKmat: mainKmat,
    subKmats,
    kmatIndex: 0,
  });

  if (definition && externalDefaultValues) {
    definition.pages.forEach((page, index) => {
      configuration.steps[index] = convertToStep(page, externalDefaultValues);
      page.substeps.forEach((substep) => {
        configuration.substeps[substep.type] = convertToSubstep(substep, kmats);
      });
    });
    configuration.description = mainResolver.resolveNullableValue(definition.description) ?? "";
    configuration.mediaUrl = mainResolver.resolveNullableValue(definition.mediaUrl) ?? "";
  }
}

function convertToStep(step: StepDefinition, defaultValues: CsticDefaultValues): LinearStep {
  return {
    ...step,
    substeps: step.substeps.map((substep) => substep.type),
    defaultValues: getDefaultValues(step.substeps, defaultValues),
    active: step.enabled,
    showResetConfirmation: step.resetRequiresConfirmation,
  };
}

function convertToSubstep(substep: SubstepDefinition, kmats: Record<KmatType, Kmat[]>): LinearSubstep {
  const kmatType = substep.kmatType ?? KmatType.Main;

  const mainKmat = getMainKmat({ kmats });
  const subKmats = [...(kmats[KmatType.SubRail] ?? []), ...(kmats[KmatType.SubCarriage] ?? [])];
  const mainResolver = new LCValueResolver({
    mainKmat,
    kmatIndex: 0,
    currentKmat: mainKmat,
    subKmats,
  });

  const filterOption = (tile: OptionDefinition) => {
    if (tile.visible === undefined) {
      return true;
    }
    return mainResolver.resolveNullableValue(tile.visible) ?? false;
  };

  const mapOption = (tile: OptionDefinition) => convertToTile(tile, mainResolver);

  const baseSubstep = {
    ...substep,
    tilesPerRow: substep.tilesPerRow ?? 1,
    name: mainResolver.resolveNullableValue(substep.name) ?? "",
    description: mainResolver.resolveNullableValue(substep.description) ?? "",
    info: mainResolver.resolveNullableValue(substep.info) ?? "",
    footerInfo: substep?.footerInfo?.map((item) => mainResolver.resolveNullableValue(item)).filter(isNonNull) ?? [],
    extraTexts: substep.extraTexts ?? {},
    tiles: (substep.tiles ?? []).filter(filterOption).map(mapOption),
    groupedTiles: (substep.groupedTiles ?? [])
      .filter((group) => mainResolver.resolveNullableValue(group.visible) ?? false)
      .map((group) => ({
        ...group,
        name: group.name ?? "",
        description: group.description ?? "",
        info: group.info ?? "",
        tiles: group.tiles.filter(filterOption).map(mapOption),
      })),
    selectedTile: null,
    value: null,
    additionalValidations: {},
    additionalData: {},
    isVisible: mainResolver.resolveNullableValue(substep.visible) ?? false,
    extraConfig: substep.extraConfig ?? {},
  };

  if (kmatType === KmatType.Main) {
    return {
      ...baseSubstep,
      kmatType: KmatType.Main,
      cstics: resolveCstics(substep, kmats, kmatType) as SubstepCstics,
    };
  } else {
    return {
      ...baseSubstep,
      kmatType: kmatType as KmatType.SubCarriage | KmatType.SubRail,
      cstics: resolveCstics(substep, kmats, kmatType) as SubstepCstics[],
    };
  }
}

function resolveCstics(
  substep: SubstepDefinition,
  kmatsMap: Record<KmatType, Kmat[]>,
  kmatType: KmatType,
): SubstepCstics | SubstepCstics[] {
  const kmats = kmatsMap[kmatType];
  const mainKmat = getMainKmat({ kmats: kmatsMap });
  const subKmats = [...(kmatsMap[KmatType.SubRail] ?? []), ...(kmatsMap[KmatType.SubCarriage] ?? [])];

  const cstics = kmats.map((currentKmat, index) => {
    const resolver = new LCValueResolver({
      mainKmat,
      kmatIndex: index,
      currentKmat,
      subKmats,
    });

    return {
      input: resolveMap(resolver, substep.inputCstics ?? {}, resolveCsticDefinition),
      helper: resolveMap(resolver, substep.helperCstics ?? {}, resolveCsticDefinition),
    };
  });

  return kmatType === KmatType.Main ? cstics[0] : cstics;
}

function convertToTile(option: OptionDefinition, resolver: LCValueResolver): SubstepTile {
  const toRecord = (cstics: CsticToSet[]) => {
    return toMapFn(
      cstics,
      ({ csticName }) => csticName,
      ({ value }) => value,
    );
  };

  const cstics = resolver.resolveList(option.cstics);
  const csticsToEnable = cstics.filter((cstic) => !!cstic.value);

  return {
    name: option.name ?? "",
    description: option.description ?? "",
    imageUrl: option.imageUrl ?? "",
    info: resolver.resolveNullableValue(option.info) ?? "",

    cstics: toRecord(cstics),
    csticsToEnable: option.overrideCsticsToEnable ? {} : toRecord(csticsToEnable),

    isDisabledExternally: resolver.resolveNullableValue(option.disabled) ?? false,
  };
}

function getDefaultValues(substeps: SubstepDefinition[], defaultValuesMapping: CsticDefaultValues): CsticDefaultValues {
  const defaultValues: CsticDefaultValues = {};
  (substeps ?? [])
    .flatMap((substep) => substep.csticsToReset ?? [])
    .forEach((csticName) => {
      const resolvedValue = defaultValuesMapping[csticName] || null;
      defaultValues[csticName] = resolvedValue;
    });
  return defaultValues;
}

function resolveMap<T, V>(
  resolver: LCValueResolver,
  map: Record<string, T>,
  mappingFunc: (value: T, resolver: LCValueResolver) => V,
): Record<string, V> {
  return Object.fromEntries(Object.entries(map).map(([key, value]) => [key, mappingFunc(value, resolver)]));
}

function resolveCsticDefinition(value: CsticDefinition, resolver: LCValueResolver): ResolvedCsticDefinition {
  return {
    name: value.name,
    kmatType: value.kmatType ?? KmatType.Main,
    kmatIndex: resolver.kmatIndex,
    disabled: resolver.resolveNullableValue(value.disabled) ?? false,
    visible: resolver.resolveNullableValue(value.visible) ?? true,
    required: resolver.resolveNullableValue(value.required) ?? false,
  };
}
